import React, { useContext, useMemo, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { ContextMenuState, OverlayMenuEnum, OverlayMenuState, TankPlan } from 'src/types/ViewModel/Vessel/TankPlan';
import { MenuPosition } from 'src/types/ViewModel/Stowage/MenuPosition';
import { OperationListColumns } from 'src/types/ViewModel/Stowage/OperationListColumns';
import { SortStowageColumnEnum } from 'src/enums/stowage/sortStowageColumnEnum';
import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { TankPlanFilter } from 'src/enums/stowage/tankPlanFilter';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { ColumnWidths } from 'src/types/ViewModel/Stowage/ColumnWidths';
import { getSavedColumnWidths } from 'src/components/stowage/operationListView/utils/columnWidthUtils';
import { ColumnConfig } from 'src/components/stowage/operationListView/utils/columnConfig';

const defaultContextMenuState: ContextMenuState = {
    visible: false,
    tank: null,
    cargoContents: [],
    cargoOperationGuid: crypto.randomUUID(),
    berthCall: null,
    x: 0,
    y: 0,
    violations: null,
};

type StowageTankPlanContextType = {
    tankPlan: TankPlan;
    setTankPlan: (tankPlan: TankPlan | null) => void;
    selectedTankPlanCondition: TankPlanCondition;
    setSelectedTankPlanCondition: (condition: TankPlanCondition) => void;
    reportTankPlanCondition: TankPlanCondition;
    setReportTankPlanCondition: (condition: TankPlanCondition) => void;
    selectedTankPlanFilters: TankPlanFilter[];
    setSelectedTankPlanFilters: (filters: TankPlanFilter[]) => void;
    tankPlanZoomInScale: number | null;
    setTankPlanZoomInScale: (value: number | null) => void;
    tankPlanZoomOutScale: number | null;
    setTankPlanZoomOutScale: (value: number | null) => void;
    tankPlanReset: boolean | null;
    setTankPlanReset: (value: boolean | null) => void;
    tankPlanMove: boolean | null;
    setTankPlanMove: (value: boolean | null) => void;
    useDynamicTankScaling: boolean;
    setUseDynamicTankScaling: (value: boolean) => void;
    zoomedTank: Tank | null;
    setZoomedTank: (tank: Tank | null) => void;
    tankContextMenuState: ContextMenuState;
    setTankContextMenuState: (contextMenuState: ContextMenuState) => void;
    operationListContextMenuState: ContextMenuState;
    setOperationListContextMenuState: (contextMenuState: ContextMenuState) => void;
    berthOperationListContextMenuState: ContextMenuState;
    setBerthOperationListContextMenuState: (contextMenuState: ContextMenuState) => void;
    violationListContextMenuState: ContextMenuState;
    setViolationListContextMenuState: (contextMenuState: ContextMenuState) => void;
    tankPlanBottomContextMenuState: ContextMenuState;
    setTankPlanBottomContextMenuState: (contextMenuState: ContextMenuState) => void;
    pageHeaderContextMenuState: ContextMenuState;
    setPageHeaderContextMenuState: (contextMenuState: ContextMenuState) => void;
    overlayMenuState: OverlayMenuState;
    setOverlayMenuState: (overlayMenuState: OverlayMenuState) => void;
    rotationMenuOpen: MenuPosition | null;
    setRotationMenuOpen: (menu: MenuPosition | null) => void;
    operationListColumns: OperationListColumns | null;
    setOperationListColumns: (op: OperationListColumns | null) => void;
    operationListSortColumn: SortStowageColumnEnum;
    setOperationListSortColumn: (nr: SortStowageColumnEnum) => void;
    isOperationListSortDesc: boolean | null;
    setIsOperationListSortDesc: (isVisible: boolean | null) => void;
    operationListColumnWidths: ColumnWidths;
    setOperationListColumnWidths: (widths: ColumnWidths) => void;
    orderedColumns: ColumnConfig[];
    setOrderedColumns: (columns: ColumnConfig[]) => void;
};

export const StowageTankPlanContext = React.createContext<StowageTankPlanContextType>(undefined!);

export const StowageTankPlanProvider = ({ children }: ContextProps) => {
    const [tankPlan, setTankPlan] = useState<TankPlan>(null);
    const [selectedTankPlanCondition, setSelectedTankPlanCondition] = useState<TankPlanCondition>(TankPlanCondition.Departure);
    const [reportTankPlanCondition, setReportTankPlanCondition] = useState<TankPlanCondition | null>(null);
    const [selectedTankPlanFilters, setSelectedTankPlanFilters] = useState<TankPlanFilter[]>([]);
    const [tankPlanZoomInScale, setTankPlanZoomInScale] = useState<number | null>(1);
    const [tankPlanZoomOutScale, setTankPlanZoomOutScale] = useState<number | null>(1);
    const [tankPlanReset, setTankPlanReset] = useState<boolean | null>(null);
    const [tankPlanMove, setTankPlanMove] = useState<boolean | null>(false);
    const [useDynamicTankScaling, setUseDynamicTankScaling] = useState<boolean>(false);
    const [zoomedTank, setZoomedTank] = useState<Tank | null>(null);
    const [tankContextMenuState, setTankContextMenuState] = useState<ContextMenuState>({ ...defaultContextMenuState });
    const [operationListContextMenuState, setOperationListContextMenuState] = useState<ContextMenuState>({ ...defaultContextMenuState });
    const [berthOperationListContextMenuState, setBerthOperationListContextMenuState] = useState<ContextMenuState>({
        ...defaultContextMenuState,
    });
    const [violationListContextMenuState, setViolationListContextMenuState] = useState<ContextMenuState>({ ...defaultContextMenuState });
    const [tankPlanBottomContextMenuState, setTankPlanBottomContextMenuState] = useState<ContextMenuState>({ ...defaultContextMenuState });
    const [pageHeaderContextMenuState, setPageHeaderContextMenuState] = useState<ContextMenuState>({ ...defaultContextMenuState });
    const [overlayMenuState, setOverlayMenuState] = useState<OverlayMenuState>({
        visible: false,
        type: OverlayMenuEnum.TankDetails,
        tank: null,
        cargoContents: null,
    });
    const [rotationMenuOpen, setRotationMenuOpen] = useState<MenuPosition | null>(null);
    const [operationListColumns, setOperationListColumns] = useState<OperationListColumns | null>(null);
    const [operationListSortColumn, setOperationListSortColumn] = useState<SortStowageColumnEnum>(SortStowageColumnEnum.TradeName);
    const [isOperationListSortDesc, setIsOperationListSortDesc] = useState<boolean | null>(null);
    const [operationListColumnWidths, setOperationListColumnWidths] = useState<ColumnWidths>(getSavedColumnWidths());
    const [orderedColumns, setOrderedColumns] = useState<ColumnConfig[]>([]);

    const value = useMemo(
        () => ({
            tankPlan,
            setTankPlan,
            selectedTankPlanCondition,
            setSelectedTankPlanCondition,
            reportTankPlanCondition,
            setReportTankPlanCondition,
            selectedTankPlanFilters,
            setSelectedTankPlanFilters,
            tankPlanZoomInScale,
            setTankPlanZoomInScale,
            tankPlanZoomOutScale,
            setTankPlanZoomOutScale,
            tankPlanReset,
            setTankPlanReset,
            tankPlanMove,
            setTankPlanMove,
            useDynamicTankScaling,
            setUseDynamicTankScaling,
            zoomedTank,
            setZoomedTank,
            tankContextMenuState,
            setTankContextMenuState,
            operationListContextMenuState,
            setOperationListContextMenuState,
            berthOperationListContextMenuState,
            setBerthOperationListContextMenuState,
            violationListContextMenuState,
            setViolationListContextMenuState,
            tankPlanBottomContextMenuState,
            setTankPlanBottomContextMenuState,
            pageHeaderContextMenuState,
            setPageHeaderContextMenuState,
            overlayMenuState,
            setOverlayMenuState,
            rotationMenuOpen,
            setRotationMenuOpen,
            operationListColumns,
            setOperationListColumns,
            operationListSortColumn,
            setOperationListSortColumn,
            isOperationListSortDesc,
            setIsOperationListSortDesc,
            operationListColumnWidths,
            setOperationListColumnWidths,
            orderedColumns,
            setOrderedColumns,
        }),
        [
            tankPlan,
            selectedTankPlanCondition,
            reportTankPlanCondition,
            selectedTankPlanFilters,
            tankPlanZoomInScale,
            tankPlanZoomOutScale,
            tankPlanReset,
            tankPlanMove,
            useDynamicTankScaling,
            zoomedTank,
            tankContextMenuState,
            operationListContextMenuState,
            berthOperationListContextMenuState,
            violationListContextMenuState,
            tankPlanBottomContextMenuState,
            pageHeaderContextMenuState,
            overlayMenuState,
            rotationMenuOpen,
            operationListColumns,
            operationListSortColumn,
            isOperationListSortDesc,
            operationListColumnWidths,
            orderedColumns,
        ],
    );

    return <StowageTankPlanContext.Provider value={value}>{children}</StowageTankPlanContext.Provider>;
};

export const useStowageTankPlanStore = () => useContext(StowageTankPlanContext);
